import { useAbility } from "@casl/react";
import type { IAlertProps } from "@fluidattacks/design";
import {
  Alert,
  Col,
  ComboBox,
  Form,
  InnerForm,
  Row,
} from "@fluidattacks/design";
import isNull from "lodash/isNull";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CheckAccessButton } from "./check-access-button";
import { CredentialsSection } from "./credentials-section";
import { Exclusions } from "./exclusions";
import { HealthCheckSection } from "./health-check-section";
import { RepositoryTour } from "./repository-tour";
import type { IRepositoryProps } from "./types";
import { UrlSection } from "./url-section";
import { formatInitialValues } from "./utils";
import { validationSchema } from "./validations";

import type { IFormValues, IRepositoryForm } from "../../../types";
import { formInitialValues } from "../utils";
import {
  authzGroupContext,
  authzPermissionsContext,
} from "context/authz/config";
import { useAudit } from "hooks/use-audit";
import { useTour } from "hooks/use-tour";

const Repository = ({
  initialValues = formInitialValues,
  isEditing,
  manyRows = false,
  modalMessages,
  onClose,
  onSubmit,
}: Readonly<IRepositoryProps>): JSX.Element => {
  const { t } = useTranslation();
  const { tours, setCompleted } = useTour();

  const prefix = "group.scope.git.repo.";
  const initialCreds =
    !isNull(initialValues.credentials) && initialValues.credentials.id !== "";

  const { addAuditEvent } = useAudit();
  useEffect((): void => {
    if (initialValues.id) addAuditEvent("Root", initialValues.id);
  }, [addAuditEvent, initialValues.id]);

  const [showGitAlert, setShowGitAlert] = useState(false);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const [isCheckedHealthCheck, setIsCheckedHealthCheck] = useState(isEditing);
  const [isGitAccessible, setIsGitAccessible] = useState(true);
  const [credExists, setCredExists] = useState(initialCreds || manyRows);
  const [disabledCredsEdit, setDisabledCredsEdit] = useState(
    initialCreds || manyRows,
  );
  const [validateGitMsg, setValidateGitMsg] = useState({
    message: "",
    type: "success",
  });

  const attributes = useContext(authzGroupContext);
  const permissions = useAbility(authzPermissionsContext);
  const hasAdvanced = attributes.can("has_advanced");
  const canAddExclusions = permissions.can("update_git_root_filter");

  // Data management
  const formattedInitialValues = formatInitialValues(initialValues);
  const showTour = !isEditing && !tours.newRoot;
  const gitRootsPermissions = {
    canAddExclusions,
    hasAdvanced,
  };

  // Form Management
  const finishTour = useCallback((): void => {
    setCompleted("newRoot");
  }, [setCompleted]);

  const confirmAndSubmit = useCallback(
    async (values: IRepositoryForm): Promise<void> => {
      setShowSubmitAlert(false);

      const formattedValues: IFormValues = {
        ...values,
        gitignore: Array.isArray(values.gitignore)
          ? values.gitignore
              .map((item): string =>
                typeof item === "object" ? item.value : item,
              )
              .filter((item): boolean => item.trim() !== "")
          : [],
      };

      return onSubmit(formattedValues);
    },
    [onSubmit],
  );

  return (
    <Form
      cancelButton={{ onClick: onClose }}
      defaultValues={formattedInitialValues}
      onSubmit={confirmAndSubmit}
      yupSchema={validationSchema(
        isEditing,
        credExists,
        gitRootsPermissions,
        isCheckedHealthCheck,
        isGitAccessible,
        manyRows,
      )}
    >
      <InnerForm<IRepositoryForm>>
        {({
          formState,
          getValues,
          setValue,
          trigger,
          register,
          watch,
          control,
          reset,
          getFieldState,
        }): JSX.Element => {
          return (
            <Fragment>
              <UrlSection
                credExists={credExists}
                defaultValues={formattedInitialValues}
                formState={formState}
                getValues={getValues}
                isEditing={isEditing}
                manyRows={manyRows}
                register={register}
                setCredExists={setCredExists}
                setIsSameHealthCheck={setIsCheckedHealthCheck}
                setValue={setValue}
                watch={watch}
              />
              <Row>
                <CredentialsSection
                  control={control}
                  credExists={credExists}
                  disabledCredsEdit={disabledCredsEdit}
                  formState={formState}
                  getFieldState={getFieldState}
                  isEditing={isEditing}
                  manyRows={manyRows}
                  register={register}
                  setCredExists={setCredExists}
                  setDisabledCredsEdit={setDisabledCredsEdit}
                  setShowGitAlert={setShowGitAlert}
                  setValue={setValue}
                  showGitAlert={showGitAlert}
                  validateGitMsg={validateGitMsg}
                  watch={watch}
                />
                <CheckAccessButton
                  credExists={credExists}
                  setIsGitAccessible={setIsGitAccessible}
                  setShowGitAlert={setShowGitAlert}
                  setValidateGitMsg={setValidateGitMsg}
                  trigger={trigger}
                  watch={watch}
                />
                <Col lg={50} md={50} sm={50}>
                  <ComboBox
                    control={control}
                    items={[
                      {
                        name: t(`${prefix}priority.level.low`),
                        value: "LOW",
                      },
                      {
                        name: t(`${prefix}priority.level.medium`),
                        value: "MEDIUM",
                      },
                      {
                        name: t(`${prefix}priority.level.high`),
                        value: "HIGH",
                      },
                      {
                        name: t(`${prefix}priority.level.critical`),
                        value: "CRITICAL",
                      },
                    ]}
                    label={t(`${prefix}priority.header`)}
                    name={"priority"}
                    tooltip={t(`${prefix}priority.toolTip`)}
                  />
                </Col>
              </Row>
              <Exclusions
                control={control}
                initialValues={formattedInitialValues}
                isEditing={isEditing}
                manyRows={manyRows}
                register={register}
                reset={reset}
                setValue={setValue}
                watch={watch}
              />
              <HealthCheckSection
                defaultValues={formattedInitialValues}
                formState={formState}
                isEditing={isEditing}
                isSameHealthCheck={isCheckedHealthCheck}
                setIsSameHealthCheck={setIsCheckedHealthCheck}
                setValue={setValue}
                watch={watch}
              />
              {!showSubmitAlert && modalMessages.message !== "" ? (
                <Alert
                  onTimeOut={setShowSubmitAlert}
                  variant={modalMessages.type as IAlertProps["variant"]}
                >
                  {modalMessages.message}
                </Alert>
              ) : undefined}
              {showTour ? (
                <RepositoryTour
                  formState={formState}
                  isGitAccessible={isGitAccessible}
                  onFinish={finishTour}
                  run={showTour}
                  watch={watch}
                />
              ) : undefined}
            </Fragment>
          );
        }}
      </InnerForm>
    </Form>
  );
};

export { Repository };
